import { Controller } from '@hotwired/stimulus';
import { transitionSteps } from '../lib/step_transition';

// Manages multi-step content within a single modal.
// Slides between steps with a CSS transition.
//
// Usage:
//   <div data-controller="modal-steps">
//     <div data-modal-steps-target="step" class="d-flex">Step 1</div>
//     <div data-modal-steps-target="step" class="d-none">Step 2</div>
//     <button data-action="modal-steps#next">Next</button>
//     <button data-action="modal-steps#back" data-modal-steps-target="backButton">
//       Back
//     </button>
//   </div>
//
// The controller toggles `d-none` / `d-flex` together so steps render
// with flex layout when visible. The optional `backButton` target is
// hidden on the first step and shown otherwise.
export default class extends Controller {
  static targets = ['step', 'backButton'];

  connect() {
    this.currentIndex = 0;
    this.pendingTimeout = null;
    this.stepTargets.forEach((step, i) => {
      if (i !== 0) {
        step.classList.remove('d-flex');
        step.classList.add('d-none');
      }
    });
    this.updateBackButtonVisibility();

    this.boundNext = () => this.next();
    this.element.addEventListener('modal-steps:next', this.boundNext);
  }

  disconnect() {
    this.element.removeEventListener('modal-steps:next', this.boundNext);
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
  }

  next() {
    if (this.currentIndex >= this.stepTargets.length - 1) return;
    this.transitionTo(this.currentIndex + 1, 'forward');
  }

  back() {
    if (this.currentIndex <= 0) return;
    this.transitionTo(this.currentIndex - 1, 'backward');
  }

  transitionTo(newIndex, direction) {
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);
    }

    const current = this.stepTargets[this.currentIndex];
    const next = this.stepTargets[newIndex];

    this.pendingTimeout = transitionSteps(current, next, direction);
    this.currentIndex = newIndex;
    this.updateBackButtonVisibility();
  }

  updateBackButtonVisibility() {
    if (!this.hasBackButtonTarget) return;

    if (this.currentIndex === 0) {
      this.backButtonTarget.classList.add('d-none');
      this.backButtonTarget.classList.remove('d-flex');
    } else {
      this.backButtonTarget.classList.remove('d-none');
      this.backButtonTarget.classList.add('d-flex');
    }
  }
}
